/**
 * User Service
 * Handles user search and lookup for features like group invitations.
 */

import { tokenManager } from '../utils/tokenManager'

export interface UserSearchResult {
  id: number
  username: string
  first_name: string
  last_name: string
  email: string
}

/** Convenience getter: full display name */
export function displayName(u: UserSearchResult): string {
  const full = `${u.first_name} ${u.last_name}`.trim()
  return full || u.username
}

export interface UserSearchResponse {
  users: UserSearchResult[]
  total: number
}

class UserService {
  private readonly baseUrl = '/v1/users'

  private async getAuthHeaders(): Promise<HeadersInit> {
    const token = await tokenManager.getAccessToken()
    if (!token) throw new Error('No access token available')
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  }

  private async makeRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
    const authHeaders = await this.getAuthHeaders()
    const response = await fetch(url, {
      ...options,
      headers: { ...authHeaders, ...options.headers }
    })
    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: string }
      throw new Error(body.error ?? `HTTP ${response.status}`)
    }
    return response.json() as Promise<T>
  }

  /**
   * Search users by username, first name, last name, or email.
   * GET /v1/users/search?q=query
   */
  async searchUsers(query: string): Promise<UserSearchResult[]> {
    if (!query || query.length < 2) return []
    const qs = new URLSearchParams({ q: query }).toString()
    const data = await this.makeRequest<UserSearchResponse | UserSearchResult[]>(
      `${this.baseUrl}/search?${qs}`
    )
    // Accept both { users: [...] } and a raw array
    if (Array.isArray(data)) return data
    return (data as UserSearchResponse).users ?? []
  }
}

export const userService = new UserService()
